/*
 * Copyright 2026 Sony Corporation
 */
import type { Run, Version } from '@ips-jit/schema';

export type TodoState = 'done' | 'active' | 'pending' | 'failed' | 'skipped';

export interface Todo {
  id: string;
  label: string;
  state: TodoState;
  detail?: string;
}

const LABELS = {
  request: 'Request',
  plan: 'Plan',
  approval: 'Approval',
  build: 'Build and verify',
  deliver: 'Run on the device',
} as const;

const ENDED: Run['status'][] = ['done', 'failed', 'rejected', 'superseded'];

/**
 * Where the latest request has got to, as a short list of stages: what has been done, what is being done and what is
 * left. `step` is the newest thing the assistant said it was doing; `version` is the version the request is building.
 */
export const todoItems = (run: Run | null, step: string | undefined, version: Version | undefined): Todo[] => {
  if (!run) {
    return (Object.keys(LABELS) as (keyof typeof LABELS)[]).map((id) => ({
      id,
      label: LABELS[id],
      state: 'pending',
      detail: id === 'request' ? 'Describe the change you want.' : undefined,
    }));
  }

  const { status } = run;
  const automatic = run.source === 'device_error';
  const ended = ENDED.includes(status);
  const answered = status === 'done' && !run.plan && !automatic;
  const built = run.version !== null || version?.status === 'built';
  const todo = (id: keyof typeof LABELS, state: TodoState, detail?: string): Todo => ({
    id,
    label: LABELS[id],
    state,
    detail,
  });

  const plan: Todo = automatic
    ? todo('plan', 'skipped', 'An automatic fix needs no plan.')
    : status === 'planning'
      ? todo('plan', 'active', step ?? 'Reading the code and planning...')
      : status === 'superseded'
        ? todo('plan', 'skipped', 'Replaced by a newer request.')
        : answered
          ? todo('plan', 'done', 'Answered.')
          : status === 'failed' && !run.plan
            ? todo('plan', 'failed', run.error ?? undefined)
            : todo('plan', 'done');

  const approval: Todo = automatic
    ? todo('approval', 'skipped', 'Automatic fixes are applied without asking.')
    : status === 'awaiting_approval'
      ? todo('approval', 'active', 'Waiting for your answer.')
      : status === 'rejected'
        ? todo('approval', 'failed', 'You rejected the plan.')
        : status === 'implementing' || (ended && run.plan)
          ? todo('approval', 'done')
          : ended
            ? todo('approval', 'skipped')
            : todo('approval', 'pending');

  const build: Todo = built
    ? todo('build', 'done', `Version ${run.version ?? version?.version}`)
    : status === 'implementing'
      ? todo('build', 'active', step ?? 'Building the app...')
      : status === 'failed' && run.plan
        ? todo('build', 'failed', run.error ?? undefined)
        : ended
          ? todo('build', 'skipped', automatic && status === 'done' ? 'No change was needed.' : undefined)
          : todo('build', 'pending');

  const state = version?.deployment_state;
  const deliver: Todo =
    state === 'applied'
      ? todo('deliver', 'done', `Version ${version?.version} is running.`)
      : state === 'failed' || state === 'rolled_back'
        ? todo('deliver', 'failed', version?.error ?? 'The device could not run it.')
        : state === 'downloading'
          ? todo('deliver', 'active', 'The device is downloading it.')
          : state === 'sent'
            ? todo('deliver', 'active', 'Sent; waiting for the device.')
            : built && !run.device_id
              ? todo('deliver', 'skipped', 'No device was chosen.')
              : built
                ? todo('deliver', 'active', 'Sending it to the device...')
                : ended
                  ? todo('deliver', 'skipped')
                  : todo('deliver', 'pending');

  return [todo('request', 'done'), plan, approval, build, deliver];
};

/** How many stages are settled: done, or not needed. */
export const settled = (items: Todo[]): number =>
  items.filter((item) => item.state === 'done' || item.state === 'skipped').length;
